import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import PaymentFormManualOrder from "../../components/PaymentFormManualOrder";
import { License, Order, Product, PaymentMethod, PaymentGateway, StatusHistoryItem, PaymentInfoItem } from "types";
import { SalesOrderStatusBadge } from "components/SalesOrderstatus";
import PaymentPanel from "components/PaymentOptionsCard";
import customerdetails from "assets/images/customerdetails.png";
import SharedOrderDetailsReturn from "components/SalesOrderDetails";
import DeleteConfirmModal from "components/ToolTips/DeleteConfirmModal";
import { showError } from "toast/toast";
import type { InvoiceType } from "components/InvoiceDocument";
import "../../assets/css/SignageOrders.css";
import { getActiveTaxType } from "components/Tax/manualtax";
import { salesOrderNeedsGetLiveTaxGate } from "components/SalesOrderDetails/salesOrderDetailsTaxAndUiHelpers";
import {
  PostVersionOrdernumber,
  Refund,
  GetVersionOrdernumber,
} from "components/specialFeeTaxCloud";
import { sendSalesOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
import {
  buildSalesOrderInvoicePdfAttachment,
} from "utils/installationOrderPdfAttachment";
import { handleApiError } from "utils/errorHandler";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import { getAuthCookie } from "../../utils/authCookies";
import { buildLoggedInPaymentPaidBy } from "../../utils/buildPaymentPaidBy";
import { base64PdfToBlob, triggerBrowserDownload } from "utils/invoiceEmailHelpers";
import {
  fetchOrderById,
  fetchPaymentGatewayBySupplier,
  fetchPaymentMethodsBySupplierAndUser,
  resolvePaymentMethodsMembershipId,
  createStripePaymentIntent,
  postOrderPayment,
  postOrderNet30Payment,
  postOrderAction,
  salesOrderUrls,
  SALES_ORDER_API_BASE,
  resolveSellerScopeOrganizationId,
} from "api/salesOrderApi";



type ProductObj = {
  product_id: string;
  signage_company_id: string;
  product: Product;
  created_by: {
    id: string;
    name: string;
  };
};

type TaxGroup = {
  id: string;
  name: string;
  status: string;
};

type TaxRule = {
  id: string;
  country: string;
  state: string;
  stateCode: string;
  county: string;
  countyCode: string;
  zipCodes: string[];
  taxRate: number;
  comments: string;
  status: string;
};

type PaymentInfo = {
  payment_id?: string;
  method?: string;
  payment_date?: string | number | Date;
  paid_by?: {
    name?: string | null;
    global_user_id?: string | null;
    membership_id?: string | null;
    organization_id?: string | null;
  } | null;
  total_amount?: number | string | null;
  amount_paid?: number | string | null;
  amount_due?: number | string | null;
};


const SignageOrderDetails = () => {
  const SearchData = () => {
    // Implement your search logic here
  };
  const { orderId } = useParams<{ orderId: string }>();
  const [loading, setLoading] = useState(true);
  const token = getAuthCookie("sessionToken");
  const membershipId = getAuthCookie("membershipId");
  const user = getAuthCookie("userName");
  const navigate = useNavigate();
  const safeNavigate = createSafeNavigate(navigate);
  const location = useLocation();
  const isSignageTeammember = location.pathname.includes('/signageteammember');
  const organizationId = getAuthCookie("organizationId");
  const effectiveSignageOrgId = organizationId || "";
  const supplierId = effectiveSignageOrgId;
  const paidByMembershipId = membershipId || "";

  const [product, setProduct] = useState<Product>();
  const [productobj, setProductObj] = useState<ProductObj>();
  const [order, setOrder] = useState<Order | null>(null);
  const [customer, setCustomer] = useState<License | null>(null);
  const baseRoute = isSignageTeammember ? '/signageteammember' : '/signage';
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<{ name: string; _id: string } | null>(null);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const sellerOrgId = useMemo(
    () =>
      resolveSellerScopeOrganizationId(supplierId, order as { seller_org_id?: string } | null),
    [order, supplierId]
  );
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
  const [payment, setPayment] = useState(false);
  const [error, setError] = useState<string>("");
  const [showGetLiveTaxModal, setShowGetLiveTaxModal] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | "">();
  // Fetch order data
  const mail = order?.customer_email;

  // Note: Do NOT initialize Elements with a SetupIntent client_secret.
  // The Payment Element below must be created with a PaymentIntent client_secret.
  // SetupIntent is created only when saving a card, inside the payment form.


  // 1) Hoist this above useEffect to reuse it:
  const fetchOrderData = async () => {
    try {
      setLoading(true);
      if (!token || !orderId) throw new Error("Token or order ID is required");
      const data = await fetchOrderById(token, orderId);
      setOrder(data);
      setTotalAmount(data?.total_amount);
    } catch (err) {
      safeConsole.error("Error fetching order data:", err);
      await handleApiError(err, {
        module: "sales_order",
        action: "fetch_order_data",
        userId: paidByMembershipId || undefined,
        url: `${SALES_ORDER_API_BASE}${salesOrderUrls.order(orderId || "")}`,
        method: "GET",
        additionalInfo: {
          orderId: orderId || null
        }
      }, token);
    } finally {
      setLoading(false);
    }
  };

  // 2) Load order whenever route id / session are ready (do not gate on legacy userId cookie).
  useEffect(() => {
    if (!token || !orderId) return;
    void fetchOrderData();
  }, [orderId, token]);

  const runPostPaymentOrderRefresh = React.useCallback(async () => {
    await fetchOrderData();
  }, [orderId, token]);

  const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);
  const [taxType, setTaxType] = useState<"manual" | "taxcloud" | "geera" | "unknown">("unknown");

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        const organizationIdForGateway = resolveSellerScopeOrganizationId(
          organizationId || effectiveSignageOrgId,
          order as { seller_org_id?: string } | null
        );
        if (!token || !organizationIdForGateway) {
          safeConsole.warn("fetchIntegrations: missing token or organizationId", {
            hasToken: !!token,
            organizationIdForGateway: organizationIdForGateway || null,
            hasOrder: !!order,
          });
          return;
        }
        const data = await fetchPaymentGatewayBySupplier(token, organizationIdForGateway);
        setIntegrations(Array.isArray(data) ? data : []);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "An unknown error occurred";
        setError(errorMessage);
        // Send error email notification and log to file
        const orgForUrl = resolveSellerScopeOrganizationId(
          organizationId || effectiveSignageOrgId,
          order as { seller_org_id?: string } | null
        );
        await handleApiError(err, {
          module: "sales_order",
          action: "fetch_payment_integrations",
          userId: paidByMembershipId || undefined,
          url: `${SALES_ORDER_API_BASE}${salesOrderUrls.paymentGateway(orgForUrl || "")}`,
          method: "GET",
          additionalInfo: {
            supplierId: effectiveSignageOrgId || null,
            organizationId: orgForUrl || null,
            isSignageTeammember
          }
        }, token);
      } finally {
        setLoading(false);
      }
    };

    if (!token) return;
    // Prefer seller org from order once loaded; otherwise cookie organizationId.
    const orderSellerOrgId = String((order as { seller_org_id?: string } | null)?.seller_org_id ?? "").trim();
    if (!orderSellerOrgId && !organizationId && !effectiveSignageOrgId) return;
    void fetchIntegrations();
  }, [
    token,
    (order as { seller_org_id?: string } | null)?.seller_org_id,
    isSignageTeammember,
    effectiveSignageOrgId,
    organizationId,
    paidByMembershipId,
  ]);

  useEffect(() => {
    const fetchTaxType = async () => {
      if (!token || !sellerOrgId) return;
      try {
        const activeType = await getActiveTaxType(token, sellerOrgId);
        const normalizedType = String(activeType || "").trim().toLowerCase();
        if (normalizedType === "taxcloud" || normalizedType === "manual" || normalizedType === "geera") {
          setTaxType(normalizedType as "manual" | "taxcloud" | "geera");
          return;
        }
        setTaxType("unknown");
      } catch {
        setTaxType("unknown");
      }
    };
    fetchTaxType();
  }, [token, sellerOrgId]);

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);


  useEffect(() => {
    const stripeInfo = integrations?.find((method) => method.gateway === "stripe");
    if (stripeInfo && stripeInfo.publishable_key) {
      const promise = loadStripe(stripeInfo.publishable_key);
      setStripePromise(promise);
    }
  }, [integrations]);

  const formik = useFormik({
    initialValues: {
      shippingMethod: "",
      paymentMethod: "",
      enable_partial_payment: false,
      partial_amount: 0,
      transactionId: "",
      total_amount: order?.total_amount,
    },
    validationSchema: Yup.object({
      userType: Yup.string().required("Buyer type is required"),
    }),
    onSubmit: async (values) => { },
  });

  // useEffect(() => {
  //   const fetchCustomerData = async () => {
  //     // console.log("🔍 Customer ID:", order?.customer_id);
  //     try {
  //       setLoading(true);
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/users/${order?.customer_id}`,
  //         {
  //           method: "GET",
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );
  //       if (!response.ok) {
  //         const errorData = await response.json().catch(() => ({ message: response.statusText }));
  //         throw new Error(errorData.message || `Failed to fetch user data: ${response.statusText}`);
  //       }

  //       const data = await response.json();
  //       if (data) {
  //         setCustomer(data);
  //       }
  //     } catch (err) {
  //       safeConsole.error("Error fetching user data:", err);
  //       // Send error email notification and log to file
  //       await handleApiError(err, {
  //         module: "sales_order",
  //         action: "fetch_customer_data",
  //         userId: paidByMembershipId || undefined,
  //         url: `${process.env.REACT_APP_API_URL}/users/${order?.customer_id}`,
  //         method: "GET",
  //         additionalInfo: {
  //           customerId: order?.customer_id || null,
  //           orderId: orderId || null
  //         }
  //       }, token);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   if (order?.customer_id) fetchCustomerData();
  // }, [order?.customer_id, token]);


  const handleDownloadInvoice = async (_t?: InvoiceType) => {
    try {
      const tokenStr = typeof window !== "undefined" ? (getAuthCookie("sessionToken") ?? "") : "";
      if (!order || !tokenStr) {
        alert("Order or session not available. Please try again.");
        return;
      }
      const salesOrderId = String((order as any)?._id || (order as any)?.id || "").trim();
      if (!salesOrderId) {
        alert("Missing sales order id.");
        return;
      }
      const safeNum = String(order?.order_number || order?.id || (order as any)?._id || "order").replace(
        /[^\w.-]+/g,
        "_"
      );
      const att = await buildSalesOrderInvoicePdfAttachment({
        salesOrderId,
        token: tokenStr,
        fileLabel: safeNum,
        pdfFetch: { maxAttempts: 6, initialDelayMs: 300, retryDelayMs: 500, perFetchTimeoutMs: 20_000 },
      });
      if (!att) {
        throw new Error("Could not fetch invoice PDF from the server.");
      }
      const [filename, b64] = Object.entries(att)[0];
      triggerBrowserDownload(base64PdfToBlob(b64), filename);
    } catch (err) {
      safeConsole.error("PDF download failed:", err);
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      alert(`PDF download failed: ${errorMessage}`);
      // Send error email notification and log to file
      await handleApiError(err, {
        module: "sales_order",
        action: "download_invoice",
        userId: paidByMembershipId || undefined,
        url: "Invoice PDF generation",
        method: "GET",
        additionalInfo: {
          orderId: orderId || null,
          orderNumber: order?.order_number || null,
          invoiceType: _t || "sales"
        }
      }, token);
    }
  };

  const [orderIds, setOrderId] = useState<string>("");

  useEffect(() => {
    if (!token || !order) return;

    async function fetchOrderId() {
      try {
        const order_id = await GetVersionOrdernumber(
          token || "",
          order?.order_number || "",  // no need for "||"
          order?.order_type || "sales"  // no need for "||"
        );
        const orderId = (order_id as any)?.full_number || (order_id as any)?.order_number || "";
        setOrderId(orderId);
      } catch (err) {
        safeConsole.error("Failed to get version order number:", err);
        // Send error email notification and log to file
        await handleApiError(err, {
          module: "sales_order",
          action: "get_version_order_number",
          userId: paidByMembershipId || undefined,
          url: `${SALES_ORDER_API_BASE}/utils/get-version-order-number`,
          method: "GET",
          additionalInfo: {
            orderId: orderId || null,
            orderNumber: order?.order_number || null,
            orderType: order?.order_type || "sales"
          }
        }, token);
      }
    }

    fetchOrderId(); // 👈 IMPORTANT: CALL IT
  }, [token, order]);


  const handleTaxRefundOnCancel = async () => {
    if (!order || !token) return;

    const taxSummary = order?.order_tax_summary;
    const taxSummaryAny = taxSummary as Record<string, any> | undefined;

    if (!orderIds) return;

    const taxStatus = String(taxSummary?.tax_status || "").toLowerCase();

    if (taxStatus !== "complete") return;

    try {
      const orderType = taxSummaryAny?.order_type || (order as any)?.order_type || "sales";

      const refundSuccess = await Refund(token, orderIds);
      safeConsole.log("[SIGNAGE:CANCEL] refund result:", { refundSuccess });

      if (refundSuccess) {
        const versionResponse = await PostVersionOrdernumber(token, order?.order_number, orderType);
        if (!versionResponse) throw new Error("TaxCloud refund failed");

        await GetVersionOrdernumber(token, orderIds, orderType);
      } else {
        safeConsole.log("[SIGNAGE:CANCEL] Refund NOT successful → skipping PostVersionOrdernumber");
      }
    } catch (error) {
      safeConsole.error("[SIGNAGE:CANCEL] Tax refund flow failed", error);
      // Send error email notification and log to file
      await handleApiError(error, {
        module: "sales_order",
        action: "tax_refund_on_cancel",
        userId: paidByMembershipId || undefined,
        url: "TaxCloud Refund API",
        method: "POST",
        additionalInfo: {
          orderId: orderId || null,
          orderNumber: order?.order_number || null,
          orderIds: orderIds || null,
          taxStatus: taxStatus
        }
      }, token);
    }
  };

  const resolveSignageIdForEmail = (orderData?: Order | null): string => {
    return String(
      orderData?.signage_id ||
      (orderData as { seller_org_id?: string } | null | undefined)?.seller_org_id ||
      effectiveSignageOrgId ||
      ""
    ).trim();
  };

  const shouldSendSalesStatusEmail = (ordStatus: number): boolean => {
    // Policy-aligned silent statuses.
    if (ordStatus === 101 || ordStatus === 300) return false;
    return ordStatus === 301 || ordStatus === 302 || ordStatus === 400;
  };

  // Helper function to send status update emails via centralized engine
  // const sendStatusUpdateEmails = async (ordStatus: number, updatedOrder?: Order | null) => {
  //   const orderToUse = updatedOrder || order;
  //   if (!orderToUse) return;

  //   if (!shouldSendSalesStatusEmail(ordStatus)) {
  //     safeConsole.log("sendStatusUpdateEmails: skipped by status policy", { ordStatus });
  //     return;
  //   }

  //   try {
  //     const orderIdForEmail = String(orderToUse.id || (orderToUse as any)._id || "").trim();
  //     const signageIdForEmail = resolveSignageIdForEmail(orderToUse);
  //     if (!orderIdForEmail || !signageIdForEmail || !token) {
  //       safeConsole.warn("sendStatusUpdateEmails: missing identifiers", {
  //         hasOrderId: !!orderIdForEmail,
  //         hasSignageId: !!signageIdForEmail,
  //         hasToken: !!token,
  //       });
  //       return;
  //     }

  //     const firstCharge = Array.isArray(orderToUse.order_charges) ? orderToUse.order_charges[0] : null;
  //     const shippingOptions = Array.isArray(firstCharge?.shipping_option) ? firstCharge.shipping_option : [];
  //     const isReadyForPickup = ordStatus === 301 && shippingOptions.some((row: any) => row?.is_customer_pickup === true);
  //     const hasTrackingNumber = Boolean(
  //       String((orderToUse as any)?.shipping_method?.tracking_number || "").trim()
  //     );
  //     const triggerName = isReadyForPickup
  //       ? "Signage Sales order ready for pickup update"
  //       : ordStatus === 301
  //         ? (hasTrackingNumber
  //           ? "Signage Sales order shipped with tracking"
  //           : "Signage Sales order shipped manual")
  //         : "Signage Sales order status update";

  //     await sendSalesOrderTriggerEmail({
  //       triggerName,
  //       orderId: orderIdForEmail,
  //       signageId: signageIdForEmail,
  //       token,
  //       salesStatusCode: ordStatus,
  //     });
  //   } catch (emailError) {
  //     safeConsole.warn(`Failed to send order status update emails for status ${ordStatus}:`, emailError);
  //     // Don't block status update if email fails
  //   }
  // };

  const requireGetLiveTaxBeforeProceed = useCallback((): boolean => {
    if (salesOrderNeedsGetLiveTaxGate(taxType, order?.order_tax_summary?.tax_status)) {
      setShowGetLiveTaxModal(true);
      return false;
    }
    return true;
  }, [order?.order_tax_summary?.tax_status, taxType]);

  const handlePaymentMethodSelect = useCallback(
    (method: { name: string; _id: string }) => {
      const clearing = !String(method?.name || "").trim() && !String(method?._id || "").trim();
      if (clearing) {
        setSelectedPaymentMethod(method);
        return;
      }
      if (!requireGetLiveTaxBeforeProceed()) return;
      setSelectedPaymentMethod(method);
    },
    [requireGetLiveTaxBeforeProceed]
  );

  const handleOrderStatus = async (ordStatus: number) => {
    /** Same rule as `SalesOrderDetails` `hasCustomerPickupShipping` — status 300: `customer_pickup` vs `shipping`. */
    let isCustomerPickupShipping = false;
    try {
      const firstCharge = Array.isArray((order as any)?.order_charges) ? (order as any).order_charges[0] : null;
      const shippingOptions = Array.isArray(firstCharge?.shipping_option) ? firstCharge.shipping_option : [];
      isCustomerPickupShipping = shippingOptions.some((row: any) => row?.is_customer_pickup === true);

      if ((ordStatus === 300 || ordStatus === 301) && !requireGetLiveTaxBeforeProceed()) {
        return;
      }

      setLoading(true);
      if (!token || !orderId) return;

      let action = "";
      let body: Record<string, unknown> | undefined;

      if (ordStatus === 400) {
        await handleTaxRefundOnCancel();
        action = "cancel";
      } else if (ordStatus === 101) {
        action = "finalize";
        body = {};
      } else if (ordStatus === 300) {
        action = isCustomerPickupShipping ? "customer_pickup" : "shipping";
        if (!isCustomerPickupShipping) {
          const shippingMethod = (order as any)?.shipping_method ?? {};
          const shippingAmount = Number((order as any)?.shipping_amount ?? 0);
          body = {
            carrier_name:
              String(shippingMethod?.carrier_name ?? shippingMethod?.carrier ?? "")
                .trim() || undefined,
            service_name:
              String(shippingMethod?.service_name ?? shippingMethod?.service ?? "")
                .trim() || undefined,
            tracking_number:
              String(
                shippingMethod?.tracking_number ?? shippingMethod?.tracking ?? ""
              )
                .trim() || undefined,
            cost: Number.isFinite(shippingAmount) && shippingAmount > 0 ? shippingAmount : undefined,
          };
        } else {
          body = {};
        }
      } else if (ordStatus === 301) {
        action = "shipped";
      } else if (ordStatus === 302) {
        action = "delivered";
      } else {
        throw new Error("Invalid order status");
      }

      const result = await postOrderAction(
        token,
        orderId,
        action,
        ordStatus === 400 || ordStatus === 301 || ordStatus === 302 ? undefined : body
      );
      toast.success(result.message || "Order update successful:");
      
      let updatedOrder = order;
      try {
        updatedOrder = await fetchOrderById(token, orderId);
      } catch (fetchErr) {
        safeConsole.warn("Failed to fetch updated order for email:", fetchErr);
      }
      
      // Send status update emails for ALL status changes (101, 300, 301, 302, 400)
      // await sendStatusUpdateEmails(ordStatus, updatedOrder);
      
      safeNavigate(0);
    } catch (error) {
      safeConsole.error("Error updating order:", error);
      const statusAction =
        ordStatus === 400
          ? "cancel"
          : ordStatus === 101
            ? "finalize"
            : ordStatus === 300
              ? isCustomerPickupShipping
                ? "customer_pickup"
                : "shipping"
              : ordStatus === 301
                ? "shipped"
                : ordStatus === 302
                  ? "delivered"
                  : "unknown";
      await handleApiError(error, {
        module: "sales_order",
        action: "update_order_status",
        userId: paidByMembershipId || undefined,
        url: orderId
          ? `${SALES_ORDER_API_BASE}${salesOrderUrls.orderStatus(orderId, statusAction)}`
          : `${SALES_ORDER_API_BASE}${salesOrderUrls.order(orderId || "")}`,
        method: "PUT",
        additionalInfo: {
          orderId: orderId || null,
          orderNumber: order?.order_number || null,
          orderStatus: ordStatus,
          statusAction:
            ordStatus === 400
              ? "cancel"
              : ordStatus === 101
                ? "finalize"
                : ordStatus === 300
                  ? isCustomerPickupShipping
                    ? "customer_pickup"
                    : "shipping"
                  : ordStatus === 301
                    ? "shipped"
                    : ordStatus === 302
                      ? "delivered"
                      : "unknown",
        }
      }, token);
      showError(`Failed to update order status: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOrderFinalStatus = async (ordStatus: string) => {
    try {
      setLoading(true);
      if (!token || !orderId) return;

      const data = await postOrderAction(token, orderId, ordStatus);
      toast.success(data.message);
      safeNavigate(0);
    } catch (error) {
      safeConsole.error("Error updating order:", error);
      alert(error instanceof Error ? error.message : "Failed to update order.");
      await handleApiError(error, {
        module: "sales_order",
        action: "update_order_final_status",
        userId: paidByMembershipId || undefined,
        url: `${SALES_ORDER_API_BASE}${salesOrderUrls.orderStatus(orderId || "", ordStatus)}`,
        method: "PUT",
        additionalInfo: {
          orderId: orderId || null,
          orderNumber: order?.order_number || null,
          finalStatus: ordStatus
        }
      }, token);
      showError(`Failed to update order: ${error instanceof Error ? error.message : "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      const id = resolvePaymentMethodsMembershipId(order);
      const orgId =
        resolveSellerScopeOrganizationId(
          effectiveSignageOrgId,
          order as { seller_org_id?: string } | null
        ) || effectiveSignageOrgId;
      if (!id || !orgId || !token) {
        safeConsole.warn("fetchPaymentMethods: Missing id or organizationId", {
          id,
          orgId,
          hasToken: !!token,
        });
        return;
      }
      try {
        const data = await fetchPaymentMethodsBySupplierAndUser(token, orgId, id);
        setPaymentMethod(Array.isArray(data) ? data : []);
      } catch (err) {
        safeConsole.error("Error fetching payment methods:", err);
        await handleApiError(err, {
          module: "sales_order",
          action: "fetch_payment_methods",
          userId: paidByMembershipId || undefined,
          url: `${SALES_ORDER_API_BASE}${salesOrderUrls.paymentMethods(orgId || "", id || "")}`,
          method: "GET",
          additionalInfo: {
            supplierId: orgId || null,
            membershipId: id || null,
            inventorySource: order?.inventory_source || null
          }
        }, token);
      } finally {
        setLoading(false);
      }
    };

    // Do not gate on legacy userId cookie — use order membership + seller org.
    if (order && (order.inventory_source || resolvePaymentMethodsMembershipId(order))) {
      void fetchPaymentMethods();
    }
  }, [
    token,
    order,
    order?.inventory_source,
    (order as { buyer_membership_id?: string } | null)?.buyer_membership_id,
    (order as { customer_membership_id?: string } | null)?.customer_membership_id,
    (order as { agent_membership_id?: string } | null)?.agent_membership_id,
    (order as { team_member_membership_id?: string } | null)?.team_member_membership_id,
    order?.agent_id,
    order?.team_member_id,
    (order as { seller_org_id?: string } | null)?.seller_org_id,
    isSignageTeammember,
    effectiveSignageOrgId,
    paidByMembershipId,
  ]);

  // Helper function to send payment applied emails
  const normalizePaymentMethodName = (name: string): string =>
    String(name || "").trim().toLowerCase().replace(/\s+/g, "_");
  const shouldSkipPaymentAppliedEmail = (paymentMethodName: string, isDynamicMethod: boolean): boolean => {
    const methodKey = normalizePaymentMethodName(paymentMethodName);
    return methodKey === "store_paid" || isDynamicMethod;
  };

  // const sendPaymentAppliedEmails = async (
  //   updatedOrder: any,
  //   paymentAmountValue: number,
  //   paymentMethodName: string,
  //   fromEmail: string,
  //   isDynamicMethod: boolean = false
  // ) => {
  //   if (!fromEmail || !updatedOrder) {
  //     safeConsole.warn("sendPaymentAppliedEmails: Missing fromEmail or updatedOrder", { fromEmail: !!fromEmail, updatedOrder: !!updatedOrder });
  //     return;
  //   }
  //   if (shouldSkipPaymentAppliedEmail(paymentMethodName, isDynamicMethod)) {
  //     safeConsole.log("sendPaymentAppliedEmails: skipped for payment method", { paymentMethodName, isDynamicMethod });
  //     return;
  //   }
    
  //   try {
  //     safeConsole.log("sendPaymentAppliedEmails: centralized trigger send", {
  //       orderId: updatedOrder.id || updatedOrder._id,
  //       paymentAmountValue,
  //       paymentMethodName,
  //     });
  //     const orderIdForEmail = String(updatedOrder?.id || updatedOrder?._id || "").trim();
  //     const signageIdForEmail = resolveSignageIdForEmail(updatedOrder);
  //     if (!orderIdForEmail || !signageIdForEmail || !token) return;
  //     await sendSalesOrderTriggerEmail({
  //       triggerName: "Signage Payment applied for order",
  //       orderId: orderIdForEmail,
  //       signageId: signageIdForEmail,
  //       token,
  //       fromEmail,
  //       paymentMethodName,
  //       includeOrderPdfAttachment: true,
  //     });
  //   } catch (emailError) {
  //     safeConsole.error("Failed to send payment applied emails:", emailError);
  //     // Don't block payment success if email fails, but log the error for debugging
  //   }
  // };

  const completeOrder = async (method: { name: string; _id: string }) => {
    if (!requireGetLiveTaxBeforeProceed()) return;
    safeConsole.log("method", method);
    setLoading(true);
    let paymentSucceeded = false;

    try {
      const payments = Array.isArray(order?.payment_info) ? order?.payment_info : [];
      const totalPaid = payments.reduce(
        (acc: number, payment: { amount_paid?: number | string }) =>
          acc + Number(payment?.amount_paid || 0),
        0
      );

      const selectedPaymentMethod = paymentMethod.find((pm) => pm._id === method._id);
      const isCreditPayment = selectedPaymentMethod?.is_credit || false;

      const totalAmountValue = Number(order?.total_amount || 0);
      const remainingAmount = Math.max(totalAmountValue - totalPaid, 0);

      const latestAmountDue = Number(payments?.[0]?.amount_due || 0);
      const lastAmountDue = latestAmountDue > 0 ? latestAmountDue : remainingAmount || totalAmountValue;

      const partialRaw = formik?.values?.partial_amount;
      const parsedPartial = Number(partialRaw);
      const partialAmount = Number.isNaN(parsedPartial) ? 0 : parsedPartial;
      const isStorePaidMethod = method.name === "store_paid";
      const isDynamicMethod = method._id !== method.name;
      // store_paid is always full payment (no partial); cheque/bank_transfer can use partial
      const actualAmountPaid = isStorePaidMethod
        ? remainingAmount
        : partialAmount > 0
        ? partialAmount
        : lastAmountDue;

      const from = process.env.REACT_APP_FROM_EMAIL2;

      const offlineStaticMethods = ["cheque", "bank_transfer", "store_paid"];
      if (offlineStaticMethods.includes(method.name)) {
        if (!formik.values.transactionId || formik.values.transactionId.trim() === "") {
          toast.error("Transaction ID is required");
          setLoading(false);
          return;
        }

        await postOrderNet30Payment(token || "", String(order?.id || (order as any)._id), {
              payment_id: formik.values.transactionId || "",
              method: method.name,
              amount_paid: actualAmountPaid,
              total_amount: lastAmountDue,
              amount_due: Number(
                Math.max(0, Number(lastAmountDue) - Number(actualAmountPaid)).toFixed(2)
              ),
              payment_date: new Date().toISOString(),
              paid_by: buildLoggedInPaymentPaidBy({
                name: user,
                membership_id: paidByMembershipId,
              }),
              payment_status: order?.payment_status ?? "",
            });
        
        // Wait a moment for backend to process payment before fetching updated order
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Fetch updated order to get latest payment info (with retry)
        let updatedOrder = order;
        let retries = 3;
        while (retries > 0) {
          try {
            const fetchedOrder = await fetchOrderById(
              token || "",
              String(order?.id || (order as any)._id)
            );
            if (Array.isArray(fetchedOrder.payment_info) && fetchedOrder.payment_info.length > 0) {
              updatedOrder = fetchedOrder;
              break;
            } else if (retries === 1) {
              updatedOrder = fetchedOrder;
            }
          } catch (fetchErr) {
            safeConsole.warn(`Failed to fetch updated order (retries left: ${retries - 1}):`, fetchErr);
          }
          if (retries > 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          retries--;
        }
        
        safeConsole.log("updatedOrder", updatedOrder);  
        // Send "Payment applied for order" emails after successful payment
        // if (from) {
        //   try {
        //     await sendPaymentAppliedEmails(updatedOrder, actualAmountPaid, method.name, from, isDynamicMethod);
        //   } catch (emailErr) {
        //     safeConsole.error("Failed to send payment emails:", emailErr);
        //     // Don't block payment success, but log the error
        //   }
        // }
      } else {
        const amountPaid = isCreditPayment ? 0 : remainingAmount;
        const amountDue = isCreditPayment ? remainingAmount : 0;
        const totalAmountPayload = Number(
          formik.values.total_amount ?? order?.total_amount ?? totalAmountValue
        );

        await postOrderPayment(token || "", String(order?.id || (order as any)._id), {
              payment_id: "",
              total_amount: totalAmountPayload,
              amount_paid: amountPaid,
              amount_due: Number(amountDue.toFixed(2)),
              payment_date: new Date().toISOString(),
              method: method.name,
              paid_by: buildLoggedInPaymentPaidBy({
                name: user,
                membership_id: paidByMembershipId,
              }),
              payment_status: order?.payment_status ?? "",
            });
        
        // Wait a moment for backend to process payment before fetching updated order
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Fetch updated order to get latest payment info (with retry)
        let updatedOrder = order;
        let retries = 3;
        while (retries > 0) {
          try {
            const fetchedOrder = await fetchOrderById(
              token || "",
              String(order?.id || (order as any)._id)
            );
            if (Array.isArray(fetchedOrder.payment_info) && fetchedOrder.payment_info.length > 0) {
              updatedOrder = fetchedOrder;
              break;
            } else if (retries === 1) {
              updatedOrder = fetchedOrder;
            }
          } catch (fetchErr) {
            safeConsole.warn(`Failed to fetch updated order (retries left: ${retries - 1}):`, fetchErr);
          }
          if (retries > 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
          retries--;
        }
        
        // Send "Payment applied for order" emails after successful payment
        // if (from) {
        //   try {
        //     await sendPaymentAppliedEmails(updatedOrder, amountPaid, method.name, from, isDynamicMethod);
        //   } catch (emailErr) {
        //     safeConsole.error("Failed to send payment emails:", emailErr);
        //     // Don't block payment success, but log the error
        //   }
        // }
      }

      toast.success("Payment submitted successfully!");
      localStorage.removeItem("cart_id");
      paymentSucceeded = true;
    } catch (error) {
      const errMessage =
        (error as Error).message || "An unknown error occurred";
      safeConsole.error("Order Failed:", errMessage);
      toast.error(`Error: ${errMessage}`);
      // Send error email notification and log to file
      await handleApiError(error, {
        module: "sales_order",
        action: "complete_order_payment",
        userId: paidByMembershipId || undefined,
        url: method.name === "online" 
          ? `${SALES_ORDER_API_BASE}${salesOrderUrls.orderPayment(String(order?.id || (order as any)._id))}`
          : `${SALES_ORDER_API_BASE}${salesOrderUrls.orderNet30Payment(String(order?.id || (order as any)._id))}`,
        method: method.name === "online" ? "PUT" : "POST",
        additionalInfo: {
          orderId: orderId || null,
          orderNumber: order?.order_number || null,
          paymentMethod: method.name,
          paymentMethodId: method._id,
          totalAmount: order?.total_amount || null
        }
      }, token);
    } finally {
      setLoading(false);
      if (paymentSucceeded) {
        safeNavigate(0);
      }
    }
  };

  const totalPaid = (order?.payment_info ?? []).reduce(
    (acc, payment) => acc + Number(payment.amount_paid || 0),
    0
  );

  const maxAllowed = (order?.payment_info ?? []).length > 0 ? Number((Number(order?.total_amount || 0) - totalPaid).toFixed(2)) : Number(order?.total_amount || 0);


  const remaingamount = ((order?.total_amount || 0) - totalPaid)

  const statusHistory: StatusHistoryItem[] = Array.isArray(order?.status_history)
    ? (order?.status_history as StatusHistoryItem[])
    : [];
  //  console.log("statusHistory", statusHistory);
  const paymentInfo: PaymentInfoItem[] = [...(order?.payment_info || [])];

  const resolveOrderStatus = (status: number | undefined): string => {
    switch (status) {
      case 100: return "Draft";
      case 101: return "Confirmed";
      case 300: return "Shipping";
      case 301: return "Shipped";
      case 302:
      case 401: return "Delivered";
      case 400: return "Cancelled";
      default: return status !== undefined ? `Status ${status}` : "Unknown";
    }
  };

  const parseDate = (input: any): string => {
    try {
      return new Date(input).toLocaleString();
    } catch {
      return "Invalid Date";
    }
  };


  useEffect(() => {
    const fetchPaymentIntent = async () => {
      // const amountToPay = (formik.values.partial_amount === 0
      //   ? (Number(order?.total_amount || 0) - totalPaid)
      //   : formik.values.partial_amount);

      // console.log("amountToPay", (formik.values.partial_amount === 0
      //   ? (Number(order?.total_amount || 0) - totalPaid)
      //   : formik.values.partial_amount));

      try {
        const supplierId = effectiveSignageOrgId;
        const organizationId = resolveSellerScopeOrganizationId(
          getAuthCookie("organizationId") || supplierId,
          order as { seller_org_id?: string } | null
        );
        if (!organizationId) {
          safeConsole.warn("fetchPaymentIntent: Missing organizationId", { organizationId });
          return;
        }
        const amountCents = Math.round(
          Number(
            formik.values.partial_amount === 0
              ? Number(order?.total_amount || 0) - totalPaid
              : formik.values.partial_amount
          ) * 100
        );
        if (!(amountCents > 0)) {
          setClientSecret(undefined);
          return;
        }
        const data = await createStripePaymentIntent(token || "", organizationId, {
              amount: amountCents,

              currency: "usd",
              order_id: String(order?.id || (order as any)?._id || ""),
              order_number: String(order?.order_number || ""),
              customer_name: `${order?.customer_first_name || ""} ${order?.customer_last_name || ""}`.trim(),
              customer_email: String(order?.customer_email || ""),
              customer_phone: String(order?.customer_phone || ""),
              payment_method_types: ["card"],
            });
        if ((data as any)?.skipped) {
          setClientSecret(undefined);
          return;
        }
        setClientSecret(data.clientSecret || data.client_secret);
      } catch (err) {
        safeConsole.error("Failed to create PaymentIntent:", err);
        // Send error email notification and log to file
        const supplierId = effectiveSignageOrgId;
        // await handleApiError(err, {
        //   module: "sales_order",
        //   action: "create_payment_intent",
        //   userId: paidByMembershipId || undefined,
        //   url: `${SALES_ORDER_API_BASE}${salesOrderUrls.stripePaymentIntent(supplierId)}`,
        //   method: "POST",
        //   additionalInfo: {
        //     supplierId: supplierId || null,
        //     orderId: orderId || null,
        //     orderNumber: order?.order_number || null,
        //     amount: formik.values.partial_amount === 0
        //       ? Number(order?.total_amount || 0) - totalPaid
        //       : formik.values.partial_amount
        //   }
        // }, token);
      }
    };

    if (stripePromise) fetchPaymentIntent();
  }, [
    stripePromise,
    formik.values.partial_amount,
    order?.total_amount,
    order?.id,
    order?.order_number,
    order?.customer_first_name,
    order?.customer_last_name,
    order?.customer_email,
    order?.customer_phone,
    totalPaid,
    isSignageTeammember,
    effectiveSignageOrgId,
    paidByMembershipId,
  ]);

  // Helpers
  const toDate = (d: any) => (d ? new Date(d) : new Date(0));
  const getPaymentStatus = (p: any) =>
    p.amount_due === 0 ? "Paid" : p.amount_paid > 0 ? "Partial Payment" : "Pending";

  // Chip styles matching Order Log design (first image): teal for paid amount/Paid, orange for Pending
  const paidAmountChipStyle: React.CSSProperties = {
    backgroundColor: "#d1fae5",
    // border: "1px solid #0d9488",
    color: "#549180",
    padding: "4px 10px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
  };
  const paidStatusChipStyle: React.CSSProperties = {
    backgroundColor: "#d1fae5",
    // border: "1px solid #0d9488",
    color: "#549180",
    padding: "4px 10px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
  };
  const pendingChipStyle: React.CSSProperties = {
    backgroundColor: "#FFF4E8",
    // border: "1px solid #ea580c",
    color: "#ea580c",
    padding: "4px 10px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
  };
  const partialChipStyle: React.CSSProperties = {
    backgroundColor: "#ffedd5",
    // border: "1px solid #d97706",
    color: "#b45309",
    padding: "4px 10px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: 600,
  };

  // Build unified timeline items
  const timelineItems = [
    // 1) Status events
    ...(statusHistory || []).map((log) => {
      // Handle date extraction from various formats
      const rawDate = log.At || log.at || new Date();
      let dateValue: string | Date;
      if (typeof rawDate === 'object' && rawDate !== null && '$date' in rawDate) {
        dateValue = (rawDate as { $date: string }).$date;
      } else {
        dateValue = rawDate as string | Date;
      }
      // Handle both PascalCase (By) and camelCase (by) formats
      const byValue = (log as any).By || (log as any).by;
      return {
        type: "status",
        at: dateValue,
        title: `Order #${order?.order_number}`,
        subtitle: `Status: ${resolveOrderStatus(log.New || log.new)}`,
        iconClass: "mdi mdi-truck-fast-outline",
        ...(byValue && { by: byValue }), // Include the by field from status_history only if it exists
      };
    }),

    // 2) Payment events – paid amount chip (teal) + status chip (teal Paid / orange Pending / amber Partial)
    ...(paymentInfo || []).map((p) => {
      const status = getPaymentStatus(p);
      const statusChipStyle =
        status === "Paid"
          ? paidStatusChipStyle
          : status === "Pending"
            ? pendingChipStyle
            : partialChipStyle;
      return {
        type: "payment",
        at: p.payment_date, // ISO date
        subtitle: (
          <div className="d-flex align-items-center flex-wrap gap-2">
            <span style={paidAmountChipStyle} className="order-card-log">
              ${Number(p.amount_paid || 0).toFixed(2)}{p.method ? ` via ${p.method}` : ""}
            </span>
            <span style={statusChipStyle}>{status}</span>
          </div>
        ),
        iconClass: "mdi mdi-credit-card-outline",
      };
    }),
  ]
    // Sort DESC by date/time
    .sort(
      (a: any, b: any) => new Date(b.at).getTime() - new Date(a.at).getTime()
    );
  const colors = ["#f4c17e", "#fd7e14", "#6f42c1", "#28a745", "#0d6efd"];


  return (
    <>
      <SharedOrderDetailsReturn
        login="signage"
        order={order}
        onBack={() => safeNavigate(`${baseRoute}/orders`)}
        onDownloadInvoice={handleDownloadInvoice}
        onOrderStatus={handleOrderStatus}
        timelineItems={timelineItems}
        colors={colors}
        parseDate={parseDate}
        baseRoute={baseRoute}
        onRefreshOrder={fetchOrderData}
        rightTopPanel={
          <>
            <PaymentPanel
              variant="signage"                          // or "brokerage" | "agent" | "teamMember" | "other"
              remainingAmount={Number(remaingamount)}
              orderStatus={order?.status ?? 0}
              paymentStatus={order?.payment_status ?? ""}
              selectedPaymentMethod={selectedPaymentMethod}
              setSelectedPaymentMethod={handlePaymentMethodSelect}
              formik={formik}                            // optional but recommended for signage
              maxAllowed={maxAllowed}
              paymentInfo={order?.payment_info ?? []}
              dynamicMethods={paymentMethod}             // [{ _id, name }]
              onSubmitOffline={(key) => completeOrder(key)}
              onlinePaymentNode={
                stripePromise && clientSecret ? (
                  <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                    <PaymentFormManualOrder
                      url="orders"
                      method="PUT"
                      // Keep redirect in SPA route to avoid full page reload/login bounce.
                      // redirectUrl={`${baseRoute}/orderDetails/${orderId}`}
                      supplierId={
                        resolveSellerScopeOrganizationId(
                          effectiveSignageOrgId,
                          order as { seller_org_id?: string } | null
                        ) ||
                        String(effectiveSignageOrgId || "").trim()
                      }
                      totalAmount={Number(remaingamount ?? order?.total_amount ?? 0)}
                      paidAmount={
                        (formik?.values?.enable_partial_payment ? Number(formik?.values?.partial_amount) : Number(order?.total_amount ?? 0) - totalPaid)
                      }
                      orderId={String(order?.id || (order as any)?._id || "")}
                      orderNumber={String(order?.order_number || "")}
                      customerName={`${order?.customer_first_name || ""} ${order?.customer_last_name || ""}`.trim()}
                      customerPhone={String(order?.customer_phone || "")}
                      selectedPaymentMethod={selectedPaymentMethod?.name || "online"}
                      skipRedirectOnSuccess
                      onPaymentSuccess={async (paymentIntentId, updatedOrderFromResponse?: any) => {
                        // console.log("Payment successful:", paymentIntentId);
                        // Don't automatically submit the form - let user decide when to proceed
                        // formik?.handleSubmit?.();
                        
                        // Send "Payment applied for order" emails after successful online payment
                        // Note: updatedOrderFromResponse may be provided by PaymentFormManualOrder if API returns it
                        try {
                          const from = process.env.REACT_APP_FROM_EMAIL2;
                          if (from && order) {
                            safeConsole.log("onPaymentSuccess: Starting email send process for payment:", paymentIntentId);
                            
                            // Use updated order from response if available, otherwise fetch it
                            let updatedOrder = updatedOrderFromResponse || order;
                            
                            // If we don't have updated order from response, fetch it with retry
                            if (!updatedOrderFromResponse) {
                              safeConsole.log("No updated order from response, fetching from API...");
                              // Wait a moment for backend to process payment before fetching updated order
                              await new Promise(resolve => setTimeout(resolve, 1000));
                              
                              let retries = 3;
                              while (retries > 0) {
                                try {
                                  const fetchedOrder = await fetchOrderById(
                                    token || "",
                                    String(order.id || (order as any)._id)
                                  );
                                  safeConsole.log("Fetched updated order, payment_info length:", fetchedOrder.payment_info?.length || 0);
                                  if (Array.isArray(fetchedOrder.payment_info) && fetchedOrder.payment_info.length > 0) {
                                    updatedOrder = fetchedOrder;
                                    safeConsole.log("Payment info found, using updated order");
                                    break;
                                  } else if (retries === 1) {
                                    updatedOrder = fetchedOrder;
                                    safeConsole.warn("Using fetched order even though payment_info is empty");
                                  }
                                } catch (fetchErr) {
                                  safeConsole.warn(`Failed to fetch updated order for email (retries left: ${retries - 1}):`, fetchErr);
                                }
                                if (retries > 1) {
                                  await new Promise(resolve => setTimeout(resolve, 500));
                                }
                                retries--;
                              }
                            } else {
                              safeConsole.log("Using updated order from payment API response");
                            }
                            
                            // Get payment amount from updated order
                            const paymentInfoArray = Array.isArray(updatedOrder.payment_info) ? updatedOrder.payment_info : [];
                            const latestPayment = paymentInfoArray.length > 0
                              ? paymentInfoArray.reduce((latest: any, current: any) => {
                                  const latestDate = new Date(latest?.payment_date || 0).getTime();
                                  const currentDate = new Date(current?.payment_date || 0).getTime();
                                  return currentDate > latestDate ? current : latest;
                                }, paymentInfoArray[0])
                              : null;
                            const paymentAmountValue = latestPayment?.amount_paid 
                              ? Number(latestPayment.amount_paid)
                              : Number(updatedOrder.total_amount || 0);
                            
                            const paymentMethodName = latestPayment?.method || selectedPaymentMethod?.name || "online";
                            const isDynamicMethod =
                              !!selectedPaymentMethod &&
                              selectedPaymentMethod._id !== selectedPaymentMethod.name;
                            
                            safeConsole.log("Calling sendPaymentAppliedEmails with:", {
                              orderId: updatedOrder.id || (updatedOrder as any)._id,
                              paymentAmountValue,
                              paymentMethodName,
                              paymentInfoLength: paymentInfoArray.length
                            });
                            
                            // await sendPaymentAppliedEmails(
                            //   updatedOrder,
                            //   paymentAmountValue,
                            //   paymentMethodName,
                            //   from,
                            //   isDynamicMethod
                            // );
                            
                            safeConsole.log("Email sending completed successfully");
                          } else {
                            safeConsole.warn("onPaymentSuccess: Missing from email or order", { from: !!from, order: !!order });
                          }
                        } catch (emailError) {
                          safeConsole.error("Failed to send payment applied emails:", emailError);
                          // Don't block payment success if email fails, but log the error
                        }
                        localStorage.removeItem("cart_id");
                        safeNavigate(0);
                      }}
                      userId={resolvePaymentMethodsMembershipId(order) || ""}
                      userEmail={order?.customer_email ?? ""}
                      paidBy_id={paidByMembershipId?.toString() ?? ""}
                      paidBy_name={user ?? ""}
                      payments_status={order?.payment_status ?? ""}
                    />
                  </Elements>
                ) : null
              }
            />
          </>
        }
      />

      <DeleteConfirmModal
        open={showGetLiveTaxModal}
        dynamicName="transaction"
        title="Confirm"
        message={
          <>
            Please click <strong>Get Live Taxes</strong> to calculate tax before sending a quote,
            processing a payment, or updating shipping status.
          </>
        }
        confirmLabel="OK"
        onCancel={() => setShowGetLiveTaxModal(false)}
        onDelete={() => setShowGetLiveTaxModal(false)}
      />
    </>
  )
};

export default SignageOrderDetails;